<template>
	<div>
		<div class="flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
			<WSubTitle text="Net electrical energy — actual vs deemed" />
			<span class="text-sm text-color-secondary">Hourly · kWh</span>
		</div>
		<div v-if="!series.length" class="dash-empty">
			<i class="mdi mdi-chart-areaspline dash-empty-icon" />
			<p>No hourly energy results for this period. Run TCM or wait for the hourly scheduler.</p>
		</div>
		<div v-else id="dash-energy-chart">
			<apexchart
				type="area"
				height="320"
				:options="chartOptions"
				:series="chartSeries"
			/>
		</div>
	</div>
</template>

<script lang="ts">
	import { defineComponent, PropType } from 'vue'
	import { DashboardEnergyPoint } from '@/brokers/DashboardBroker'

	export default defineComponent({
		name: 'DashChartEnergy',
		props: {
			series: {
				type: Array as PropType<DashboardEnergyPoint[]>,
				default: () => [],
			},
		},
		computed: {
			chartSeries(): Array<{ name: string; data: Array<{ x: number; y: number | null }> }> {
				const pts = this.series || []
				return [
					{
						name: 'NEEi,act',
						data: pts.map((p) => ({
							x: new Date(p.hour_start).getTime(),
							y: p.nee_act,
						})),
					},
					{
						name: 'NEEi,deemed',
						data: pts.map((p) => ({
							x: new Date(p.hour_start).getTime(),
							y: p.nee_deemed,
						})),
					},
				]
			},
			chartOptions(): Record<string, unknown> {
				return {
					chart: {
						type: 'area',
						stacked: false,
						height: 320,
						zoom: { type: 'x', enabled: true, autoScaleYaxis: true },
						toolbar: { autoSelected: 'zoom' },
					},
					colors: ['#0d9488', '#f59e0b'],
					dataLabels: { enabled: false },
					markers: { size: 0 },
					stroke: { curve: 'smooth', width: 2 },
					fill: {
						type: 'gradient',
						gradient: {
							shadeIntensity: 1,
							opacityFrom: 0.35,
							opacityTo: 0.05,
							stops: [0, 90, 100],
						},
					},
					yaxis: {
						title: { text: 'Energy (kWh)' },
						labels: {
							formatter: (v: number) =>
								v == null ? '' : Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 }),
						},
					},
					xaxis: { type: 'datetime' },
					tooltip: {
						shared: true,
						x: { format: 'dd MMM yyyy HH:mm' },
					},
					legend: { position: 'top' },
				}
			},
		},
	})
</script>

<style scoped>
	.dash-empty {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 2rem 1rem;
		color: var(--text-color-secondary);
		gap: 0.5rem;
		border: 1px dashed var(--surface-border);
		border-radius: 6px;
	}
	.dash-empty-icon {
		font-size: 2rem;
		opacity: 0.5;
	}
</style>
